"use client"

import { useEffect, useState } from "react"
import { usePathname } from "next/navigation"
import { isForceDarkBooking } from "@/i18n/routing"

// Observes the section currently sitting behind the top of the viewport
// and returns its declared theme. Sections opt in by setting
// `data-nav-theme="dark"` or `data-nav-theme="light"` on their root
// element — the same convention PublicNavbar uses to invert itself.
//
// Used by the mobile floating chrome (BOOTH wordmark + notifications
// bell) so they stay legible whether they're floating over the hero
// (dark) or a content section (light).

const NAV_ANCHOR = 28 // px from the top — matches PublicNavbar

export function useNavTheme(defaultTheme: "light" | "dark" = "light"): "light" | "dark" {
  const pathname = usePathname()
  // The client booking storefront is force-dark for everyone, so the floating
  // chrome over it must read dark regardless of device theme or page sections.
  const forcedDark = isForceDarkBooking(pathname)
  const [theme, setTheme] = useState<"light" | "dark">(forcedDark ? "dark" : defaultTheme)

  useEffect(() => {
    if (forcedDark) {
      setTheme("dark")
      return
    }
    setTheme(defaultTheme)
    const evaluate = () => {
      const sections = document.querySelectorAll<HTMLElement>("[data-nav-theme]")
      let active: "light" | "dark" | null = null
      sections.forEach((el) => {
        const r = el.getBoundingClientRect()
        if (r.top <= NAV_ANCHOR && r.bottom > NAV_ANCHOR) {
          const t = el.dataset.navTheme
          if (t === "dark" || t === "light") active = t
        }
      })
      setTheme(active ?? defaultTheme)
    }
    evaluate()
    window.addEventListener("scroll", evaluate, { passive: true })
    window.addEventListener("resize", evaluate)
    const raf = requestAnimationFrame(evaluate)
    return () => {
      window.removeEventListener("scroll", evaluate)
      window.removeEventListener("resize", evaluate)
      cancelAnimationFrame(raf)
    }
  }, [pathname, defaultTheme, forcedDark])

  return forcedDark ? "dark" : theme
}
